import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';

import { db } from '../../../../firebaseConfig';
import {
  buildSelectedOpponentIdentity,
  buildMatchRoomAnalytics,
  getOpponentMatchDedupeKey,
  rowPrimaryMatchesOpponent,
  rowMatchesOpponent,
} from '../utils/matchRoomAnalytics';

const buildRowKey = (row = {}) => String(
  row.matchGroupId ||
  row.mirrorId ||
  row.sourceModeId ||
  row.ModeID ||
  `${row.Team || ''}|${row.TeamName || ''}|${row.EnemyTeam || ''}|${row.Opponent || ''}|${row.VS || ''}|${row.Mode || ''}|${row.Map || ''}|${row.Date || ''}|${row.PF || ''}|${row.PA || ''}`
);

const mergeUniqueRows = (...groups) => {
  const seen = new Set();
  const rows = [];
  groups.flat().forEach((row) => {
    const key = buildRowKey(row);
    if (!key || seen.has(key)) return;
    seen.add(key);
    rows.push(row);
  });
  return rows;
};

const fetchCollectionRows = async (path) => {
  const snapshot = await getDocs(collection(db, path));
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, __sourcePath: path, ...(docSnap.data() || {}) }));
};

const getSeasonCandidates = (seasonId, seasonYear) => (
  [...new Set([seasonId, seasonYear].filter(Boolean).map((value) => String(value)))]
);

const fetchStatsRowsForSeasonCandidates = async ({ teamId, seasonCandidates, statsCollection }) => {
  const groups = await Promise.all(
    seasonCandidates.map(async (seasonKey) => {
      const basePath = `teams/${teamId}/seasons/${seasonKey}/database/${statsCollection}`;
      const modeRows = await fetchCollectionRows(`${basePath}/modes`).catch(() => []);
      if (statsCollection !== 'enemyStats') return modeRows;

      const matchGroupRows = await fetchCollectionRows(`${basePath}/matchGroups`).catch(() => []);
      return [...modeRows, ...matchGroupRows];
    })
  );
  return statsCollection === 'enemyStats' ? groups.flat() : mergeUniqueRows(...groups);
};

const dedupeOpponentRows = (rows = [], selectedOpponent = {}) => {
  const byMatch = new Map();

  rows.forEach((row) => {
    const key = getOpponentMatchDedupeKey(row);
    const existing = byMatch.get(key);
    if (!existing) {
      byMatch.set(key, row);
      return;
    }

    const currentPrimary = rowPrimaryMatchesOpponent(row, selectedOpponent);
    const existingPrimary = rowPrimaryMatchesOpponent(existing, selectedOpponent);
    if (currentPrimary && !existingPrimary) {
      byMatch.set(key, row);
    }
  });

  return Array.from(byMatch.values());
};

export const useMatchRoomAnalytics = ({
  teamId,
  seasonId,
  seasonYear,
  selectedRoom,
  opponentOptions = [],
  datasetFilter,
}) => {
  const [teamRows, setTeamRows] = useState([]);
  const [opponentRows, setOpponentRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!teamId || !seasonId || !selectedRoom) {
      setTeamRows([]);
      setOpponentRows([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    let isMounted = true;
    setLoading(true);
    setError('');

    const loadAnalyticsRows = async () => {
      const seasonCandidates = getSeasonCandidates(seasonId, seasonYear);
      const selectedOpponent = buildSelectedOpponentIdentity(selectedRoom, opponentOptions);
      const [teamStatsRows, mergedEnemyRows] = await Promise.all([
        fetchStatsRowsForSeasonCandidates({ teamId, seasonCandidates, statsCollection: 'teamStats' }),
        fetchStatsRowsForSeasonCandidates({ teamId, seasonCandidates, statsCollection: 'enemyStats' }),
      ]);

      if (!isMounted) return;

      const matchedOpponentRows = mergedEnemyRows.filter((row) => (
        rowMatchesOpponent(row, selectedOpponent)
      ));

      setTeamRows(teamStatsRows);
      setOpponentRows(dedupeOpponentRows(matchedOpponentRows, selectedOpponent));
    };

    loadAnalyticsRows()
      .catch((err) => {
        console.error('Failed to load Match Room analytics', err);
        if (!isMounted) return;
        setTeamRows([]);
        setOpponentRows([]);
        setError('Unable to load matchup analytics.');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [opponentOptions, seasonId, seasonYear, selectedRoom, teamId]);

  const analytics = useMemo(() => (
    buildMatchRoomAnalytics({
      teamRows,
      opponentRows,
      seasonKey: seasonYear || seasonId,
      datasetFilter,
    })
  ), [datasetFilter, opponentRows, seasonId, seasonYear, teamRows]);

  return {
    analytics,
    analyticsLoading: loading,
    analyticsError: error,
  };
};
